import * as uuid from "uuid";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  FormControl,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  ArrayHelpers,
  Field,
  FieldArray,
  FieldArrayRenderProps,
  useFormikContext,
} from "formik";
import { CheckboxWithLabel, Select, TextField } from "formik-mui";
import { friendlyScvInputName } from "portal/utils/atkEquipment";
import { IOLinkRegisterDevice } from "protos/atk/autotractor";
import { RigDetailsComputedFormState, RigDetailsFormState } from "../FormState";
import { typedPath } from "typed-path";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useContext } from "react";

const S = typedPath<RigDetailsFormState>();

const IoLinkReadRegisterDisplay: React.FC<{
  arrayHelpers: FieldArrayRenderProps;
  index: number;
  readOnly: boolean;
}> = ({ arrayHelpers, index, readOnly }) => {
  let specificSensorTypeSubform: JSX.Element | undefined;
  const { t } = useTranslation();
  const { values } = useFormikContext<RigDetailsFormState>();
  const { scvConnectionSelectOptions } = useContext(
    RigDetailsComputedFormState
  );

  if (
    values.atkConfig.ioLink.config.readRegisters[index]?.sensorType ===
    "scv-position"
  ) {
    specificSensorTypeSubform = (
      <FormControl fullWidth className="col-span-full">
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.measuresPositionOf"
          )}
        </Typography>
        <Field
          component={Select}
          name={
            S.atkConfig.ioLink.config.readRegisters[index]!.scvPosition
              .scvConnectionId.$path
          }
          displayEmpty
          disabled={readOnly}
        >
          {scvConnectionSelectOptions.map((option) => (
            <MenuItem key={option.connectionId} value={option.connectionId}>
              {friendlyScvInputName(t, option.implement, option.scvInput)}
            </MenuItem>
          ))}
        </Field>
      </FormControl>
    );
  } else {
    specificSensorTypeSubform = (
      <FormControl fullWidth className="col-span-full">
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.sensorName"
          )}
        </Typography>
        <Field
          component={TextField}
          name={
            S.atkConfig.ioLink.config.readRegisters[index]!.generic.name.$path
          }
          disabled={readOnly}
        />
      </FormControl>
    );
  }

  return (
    <Card
      variant="outlined"
      className="border-2 rounded-lg p-2 grid gap-2 grid-cols-2"
    >
      <FormControl fullWidth>
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.address"
          )}
        </Typography>
        <Field
          component={TextField}
          type="number"
          name={S.atkConfig.ioLink.config.readRegisters[index]!.address.$path}
          disabled={readOnly}
        />
      </FormControl>
      <FormControl fullWidth>
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.deviceId"
          )}
        </Typography>
        <Field
          component={TextField}
          type="number"
          name={S.atkConfig.ioLink.config.readRegisters[index]!.deviceId.$path}
          disabled={readOnly}
        />
      </FormControl>
      <FormControl>
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.valAt0Pct"
          )}
        </Typography>
        <Field
          component={TextField}
          type="number"
          name={
            S.atkConfig.ioLink.config.readRegisters[index]!.conversion
              .linearPctConversion.valAt0Pct.$path
          }
          disabled={readOnly}
        />
      </FormControl>
      <FormControl>
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.valAt100Pct"
          )}
        </Typography>
        <Field
          component={TextField}
          type="number"
          name={
            S.atkConfig.ioLink.config.readRegisters[index]!.conversion
              .linearPctConversion.valAt100Pct.$path
          }
          disabled={readOnly}
        />
      </FormControl>
      <FormControl fullWidth className="col-span-full">
        <Typography>
          {t(
            "views.autotractor.rigDetails.configuration.ioLink.readRegisters.sensorType"
          )}
        </Typography>
        <Field
          name={
            S.atkConfig.ioLink.config.readRegisters[index]!.sensorType.$path
          }
          component={Select}
          disabled={readOnly}
        >
          <MenuItem value={"scv-position"}>
            {t(
              "views.autotractor.rigDetails.configuration.ioLink.readRegisters.scvPositionSensor"
            )}
          </MenuItem>
          <MenuItem value={"generic"}>
            {t(
              "views.autotractor.rigDetails.configuration.ioLink.readRegisters.genericSensor"
            )}
          </MenuItem>
        </Field>
      </FormControl>
      {specificSensorTypeSubform}
      <Button
        variant="contained"
        color="error"
        className="w-min justify-self-end col-start-2"
        startIcon={<ClearIcon />}
        onClick={() => arrayHelpers.remove(index)}
        disabled={readOnly}
      >
        {t("utils.actions.delete")}
      </Button>
    </Card>
  );
};

export const IoLinkSubform: React.FC<{ readOnly: boolean }> = ({
  readOnly,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<RigDetailsFormState>();

  function newReadRegister(
    arrayHelpers: ArrayHelpers<IOLinkRegisterDevice[]>
  ): void {
    arrayHelpers.push({
      id: uuid.v4(),
      address: 0,
      deviceId: 0,
      conversion: {
        linearPctConversion: {
          valAt0Pct: 0,
          valAt100Pct: 1,
        },
      },
      sensorType: "generic",
      generic: {
        name: "",
      },
      scvPosition: {
        scvConnectionId: "",
      },
    });
  }

  return (
    <Accordion className="w-full">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {t("views.autotractor.rigDetails.configuration.ioLink.sectionHeader")}
      </AccordionSummary>
      <AccordionDetails className="grid grid-cols-2 gap-4">
        <FormControl fullWidth className="col-span-full">
          <Field
            component={CheckboxWithLabel}
            type="checkbox"
            name={S.atkConfig.ioLink.present.$path}
            Label={{
              label: t(
                "views.autotractor.rigDetails.configuration.ioLink.devicePresent"
              ),
            }}
            disabled={readOnly}
          />
        </FormControl>
        {formik.values.atkConfig.ioLink.present && (
          <>
            <FormControl fullWidth>
              <Typography>
                {t(
                  "views.autotractor.rigDetails.configuration.ioLink.ipAddress"
                )}
              </Typography>
              <Field
                component={TextField}
                name={S.atkConfig.ioLink.config.ipAddress.$path}
                disabled={readOnly}
              />
            </FormControl>
            <FormControl fullWidth>
              <Typography>
                {t(
                  "views.autotractor.rigDetails.configuration.ioLink.modbusPort"
                )}
              </Typography>
              <Field
                component={TextField}
                type="number"
                name={S.atkConfig.ioLink.config.modbusPort.$path}
                disabled={readOnly}
              />
            </FormControl>
            <FormControl
              fullWidth
              className="col-span-full gap-2"
              disabled={readOnly}
            >
              <Typography className="font-bold">
                {t(
                  "views.autotractor.rigDetails.configuration.ioLink.readRegisters.sectionHeader"
                )}
              </Typography>
              <FieldArray
                name={S.atkConfig.ioLink.config.readRegisters.$path}
                render={(arrayHelpers) => (
                  <>
                    {formik.values.atkConfig.ioLink.config.readRegisters.map(
                      (_, index) => (
                        <IoLinkReadRegisterDisplay
                          arrayHelpers={arrayHelpers}
                          key={index}
                          index={index}
                          readOnly={readOnly}
                        />
                      )
                    )}
                    <Button
                      variant="contained"
                      className="justify-self-center col-span-full"
                      onClick={() => newReadRegister(arrayHelpers)}
                      disabled={readOnly}
                    >
                      {t(
                        "views.autotractor.rigDetails.configuration.ioLink.readRegisters.new"
                      )}
                    </Button>
                  </>
                )}
              />
            </FormControl>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
